import React from 'react';
import { HomeOutlined } from '@ant-design/icons';

/**
 * Route label mapping for breadcrumb display
 */
const ROUTE_LABELS = {
  'admin': null, // Skip 'admin' segment
  'dashboard': 'Dashboard',
  'users': 'User Management',
  'reports': 'Reports',
  'analytics': 'Analytics',
  'wait-times': 'Wait Times',
  'health-outcomes': 'Health Outcomes',
  'operations': 'Operations',
  'events': 'Events',
  'population': 'Population',
  'volunteers': 'Volunteers',
  'booking': 'Booking',
  'exports': 'Exports',
  'screenings': 'Member Records',
  'member-queue': 'Intake',
  'member-records': 'Member Records',
  'screening-entry': 'Screening Entry',
  'patient-edit': 'Edit Patient',
  'case-management': 'Case Management',
  'inbox': 'Inbox',
  'audit-logs': 'Audit Logs',
  'volunteer-registrations': 'Volunteer Registrations',
  'call-logs': 'Call Logs',
  'settings': 'Settings',
  'edit': 'Edit',
  'surveys': 'Surveys',
  'responses': 'Responses',
  'new': 'New',
  'campaigns': 'Email Campaigns',
  'email-templates': 'Email Templates',
  'email-automations': 'Automations',
  'insurance-enrollments': 'Insurance'
};

/**
 * Route path mapping for navigation
 */
const ROUTE_PATHS = {
  'dashboard': '/admin/dashboard',
  'users': '/admin/users',
  'reports': '/admin/reports',
  'analytics': '/admin/analytics',
  'wait-times': '/admin/analytics/wait-times',
  'health-outcomes': '/admin/analytics/health-outcomes',
  'operations': '/admin/analytics/operations',
  'events': '/admin/events',
  'population': '/admin/analytics/population',
  'volunteers': '/admin/analytics/volunteers',
  'booking': '/admin/analytics/booking',
  'exports': '/admin/analytics/exports',
  'all-members': '/admin/all-members',
  'member-queue': '/admin/member-queue',
  'member-records': '/admin/all-members',
  'case-management': '/admin/case-management',
  'inbox': '/admin/inbox',
  'audit-logs': '/admin/audit-logs',
  'volunteer-registrations': '/admin/volunteer-registrations',
  'call-logs': '/admin/call-logs',
  'settings': '/admin/settings',
  'surveys': '/admin/surveys',
  'campaigns': '/admin/campaigns',
  'email-templates': '/admin/email-templates',
  'email-automations': '/admin/email-automations',
  'insurance-enrollments': '/admin/insurance-enrollments'
};

/**
 * Generates breadcrumbs from the current pathname
 * @param {string} pathname - Current URL path
 * @param {object} options - Optional overrides
 * @param {string} options.customLastLabel - Custom label for the last breadcrumb
 * @returns {Array} Array of breadcrumb objects { label, path?, icon? }
 */
export const generateBreadcrumbs = (pathname, options = {}) => {
  const { customLastLabel } = options;
  
  // Always start with Home
  const breadcrumbs = [
    { label: 'Home', path: '/admin/dashboard', icon: <HomeOutlined /> }
  ];
  
  // Split path into segments and filter out empty ones
  const segments = pathname.split('/').filter(Boolean);
  
  // Skip if we're on the dashboard (just show Home)
  if (segments.length <= 2 && segments.includes('dashboard')) {
    return breadcrumbs;
  }
  
  segments.forEach((segment, index) => {
    
    // Skip 'admin' segment
    if (segment === 'admin') return;
    
    // Check if this is a UUID or ID (skip displaying it, but use customLastLabel)
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
    const isMongoId = /^[0-9a-f]{24}$/i.test(segment);
    const isNumericId = /^\d+$/.test(segment);
    
    if (isUUID || isMongoId || isNumericId) {
      // If this is the last segment and we have a custom label, add it
      if (index === segments.length - 1 && customLastLabel) {
        breadcrumbs.push({ label: customLastLabel });
      }
      return;
    }
    
    // Get the label for this segment
    const label = ROUTE_LABELS[segment];
    
    // Skip if label is explicitly null
    if (label === null) return;
    
    // Skip if no label found (unknown segment)
    if (!label) {
      // Check if it's the last segment and we have a custom label
      if (index === segments.length - 1 && customLastLabel) {
        breadcrumbs.push({ label: customLastLabel });
      }
      return;
    }
    
    const isLast = index === segments.length - 1;
    
    // Use custom label for last breadcrumb if provided
    const displayLabel = isLast && customLastLabel ? customLastLabel : label;
    
    // Get path for navigation (only if not the last item)
    const navPath = isLast ? undefined : ROUTE_PATHS[segment];
    
    breadcrumbs.push({
      label: displayLabel,
      path: navPath
    });
  });
  
  return breadcrumbs;
};

export default generateBreadcrumbs;
